/**
 * LinkedIn Pages Adapter (Story 10.2)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls.
 *
 * CRITICAL differences from Facebook Pages adapter:
 * - Uses user's OAuth access token DIRECTLY (no separate Page Access Token)
 * - API base: https://api.linkedin.com/rest (NOT /v2)
 * - LinkedIn-Version header MANDATORY on every call
 * - Engagement rate as decimal (matches FB pattern)
 * - Millisecond timestamps for date ranges
 * - 12-month data retention (365 days max)
 *
 * ADR-013: Adapter Pattern for API Isolation
 */

import type {
  DimensionDefinition,
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
  RefreshedTokens,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { LINKEDIN_API_VERSION, LINKEDIN_INTEGRATION_SCOPES } from "./scopes";
import { discoverLinkedInOrganizations, type DiscoveredOrganization } from "./discovery";
import { refreshLinkedInAccessToken } from "./token-refresh";
import { parseLinkedInError } from "./errors";

// ============================================================================
// Constants
// ============================================================================

/** LinkedIn REST API base — different from discovery.ts which uses /v2 */
const LINKEDIN_REST_BASE = "https://api.linkedin.com/rest";

/** Maximum data retention for LinkedIn org stats (12 months) */
const MAX_RETENTION_DAYS = 365;

/** Maximum post URNs per batch stats request */
const BATCH_SIZE = 20;

/** Number of recent posts to fetch for Top Posts feature */
const POSTS_FETCH_LIMIT = 50;

/** Number of top posts to return after sorting */
const TOP_POSTS_LIMIT = 10;

/** Post message truncation length */
const MESSAGE_TRUNCATE_LENGTH = 200;

/** Max reactions per page (LinkedIn API limit) */
const REACTIONS_PAGE_SIZE = 100;

/** Safety cap on total reactions API calls per fetchMetrics invocation */
const MAX_REACTIONS_CALLS = 30;

// ============================================================================
// Types (local to adapter, NOT exported)
// ============================================================================

interface OrgShareStatisticsResponse {
  elements: Array<{
    totalShareStatistics: {
      uniqueImpressionsCount: number;
      clickCount: number;
      likeCount: number;
      commentCount: number;
      shareCount: number;
      impressionCount: number;
    };
    timeRange: { start: number; end: number };
    organizationalEntity: string;
  }>;
  paging?: { total?: number; links?: Array<{ rel: string; href: string }> };
}

interface OrgFollowerStatisticsResponse {
  elements: Array<{
    followerCounts: { organicFollowerCount: number; paidFollowerCount: number };
    followerGains?: { organicFollowerGain: number; paidFollowerGain: number };
    followerCountsByIndustry?: Array<{ industry: string; followerCounts: { organicFollowerCount: number } }>;
    followerCountsBySeniority?: Array<{ seniority: string; followerCounts: { organicFollowerCount: number } }>;
    followerCountsByGeo?: Array<{ geo: string; followerCounts: { organicFollowerCount: number } }>;
    followerCountsByFunction?: Array<{ function: string; followerCounts: { organicFollowerCount: number } }>;
    followerCountsByStaffCountRange?: Array<{ staffCountRange: string; followerCounts: { organicFollowerCount: number } }>;
    followerCountsByAssociationType?: Array<{ associationType: string; followerCounts: { organicFollowerCount: number } }>;
    timeRange?: { start: number; end: number };
  }>;
}

interface VideoAnalyticsResponse {
  elements: Array<{
    type: string; // VIDEO_VIEW, VIEWER, TIME_WATCHED, TIME_WATCHED_FOR_VIDEO_VIEWS
    value: number;
    entity: string;
  }>;
}

interface ReactionsResponse {
  elements: Array<{
    reactionType: string; // LIKE, PRAISE, EMPATHY, INTEREST, APPRECIATION, ENTERTAINMENT
  }>;
  paging?: { total?: number; count?: number; start?: number };
}

interface LinkedInPostsResponse {
  elements: Array<{
    id: string;
    commentary?: string;
    content?: { article?: { source: string; thumbnail?: string }; media?: { id: string } };
    publishedAt: number;
    lifecycleState: string;
  }>;
  paging?: { start: number; count: number; total?: number };
}

interface ShareStatElement {
  totalShareStatistics: {
    uniqueImpressionsCount: number;
    clickCount: number;
    likeCount: number;
    commentCount: number;
    shareCount: number;
    impressionCount: number;
  };
  share?: string;
}

interface ShareStatisticsResponse {
  elements: ShareStatElement[];
}

interface PageViewDay {
  total: number;
  desktop: number;
  mobile: number;
  overview: number;
  desktopOverview: number;
  mobileOverview: number;
  careers: number;
  desktopCareers: number;
  mobileCareers: number;
  jobs: number;
  lifeAt: number;
  unique: number;
}

interface OrgPageStatisticsResponse {
  elements: Array<{
    pageStatistics?: {
      views?: {
        allPageViews?: { pageViews?: number };
        allDesktopPageViews?: { pageViews?: number };
        allMobilePageViews?: { pageViews?: number };
        overviewPageViews?: { pageViews?: number };
        desktopOverviewPageViews?: { pageViews?: number };
        mobileOverviewPageViews?: { pageViews?: number };
        careersPageViews?: { pageViews?: number };
        desktopCareersPageViews?: { pageViews?: number };
        mobileCareersPageViews?: { pageViews?: number };
        jobsPageViews?: { pageViews?: number };
        lifeAtPageViews?: { pageViews?: number };
      };
    };
    uniquePageViews?: number;
    timeRange?: { start: number; end: number };
  }>;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Builds standard LinkedIn REST API headers.
 * Separate from discovery.ts linkedInHeaders() to avoid coupling (different base URL).
 */
function linkedInHeaders(accessToken: string): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
    "LinkedIn-Version": LINKEDIN_API_VERSION,
  };
}

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function getDayOfWeek(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return DAYS_OF_WEEK[d.getUTCDay()] ?? "Unknown";
}

/**
 * Converts a Date to epoch milliseconds at UTC midnight.
 */
function dateToEpochMs(date: Date): number {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  return d.getTime();
}

/**
 * Converts epoch milliseconds to YYYY-MM-DD string.
 */
function epochMsToDateStr(ms: number): string {
  const d = new Date(ms);
  return d.toISOString().split("T")[0]!;
}

/**
 * Clamps start date to max 365 days ago (LinkedIn data retention).
 * Returns epoch milliseconds for API calls.
 */
function clampDateRange(start: Date, end: Date): { startMs: number; endMs: number; clamped: boolean } {
  const now = new Date();
  const maxStart = new Date(now);
  maxStart.setUTCDate(maxStart.getUTCDate() - MAX_RETENTION_DAYS);

  let clamped = false;
  let effectiveStart = start;
  if (start < maxStart) {
    effectiveStart = maxStart;
    clamped = true;
  }

  return {
    startMs: dateToEpochMs(effectiveStart),
    endMs: dateToEpochMs(end) + 86_400_000, // +1 day for inclusive end
    clamped,
  };
}

/**
 * Handles LinkedIn API error responses.
 * Attempts to parse LinkedIn-specific error format, falls back to safeFetch mapping.
 */
async function handleLinkedInFetchError(
  fetchResult: { success: false; error: { code: string; message: string; retryable: boolean; retryAfterMs?: number; providerStatus?: number; providerMessage?: string } }
): Promise<AdapterResult<never>> {
  if (fetchResult.error.providerMessage) {
    const linkedInError = parseLinkedInError(
      fetchResult.error.providerMessage,
      undefined
    );
    if (linkedInError) {
      return adapterErr({
        code: linkedInError.code,
        message: sanitizeErrorMessage(linkedInError.message),
        retryable: linkedInError.retryable,
        // Prefer LinkedIn-parsed retryAfterMs, fall back to safeFetch's header-parsed value
        retryAfterMs: linkedInError.retryAfterMs ?? fetchResult.error.retryAfterMs,
        providerStatus: fetchResult.error.providerStatus,
      });
    }
  }

  return adapterErr(fetchResult.error as Parameters<typeof adapterErr>[0]);
}

/**
 * Determines post type from LinkedIn post content.
 * LinkedIn media IDs contain the type: "urn:li:video:..." vs "urn:li:image:..."
 * Falls back to IMAGE for non-article content with a media ID that isn't identifiable.
 */
function getPostType(post: LinkedInPostsResponse["elements"][0]): string {
  if (post.content?.article) return "ARTICLE";
  if (post.content?.media?.id) {
    // LinkedIn media URNs encode the type: urn:li:video:123, urn:li:image:456
    const mediaId = post.content.media.id.toLowerCase();
    if (mediaId.includes(":video:")) return "VIDEO";
    return "IMAGE";
  }
  if (post.content) return "IMAGE";
  return "NONE";
}

/**
 * Returns the ISO 8601 week number for a given UTC date.
 */
function getISOWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

/**
 * Truncates a string to a max length, appending "..." if truncated.
 */
function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen) + "...";
}

// ============================================================================
// Metric Definitions (Subtask 1.2)
// ============================================================================

export const LINKEDIN_PAGES_METRICS: MetricDefinition[] = [
  // Followers (4)
  { key: "followers", label: "Followers", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "follower_change", label: "Follower Change", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "organic_follower_gain", label: "Organic Follower Gain", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "paid_follower_gain", label: "Paid Follower Gain", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  // Page Views (12)
  { key: "page_views_total", label: "Page Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "page_views_desktop", label: "Desktop Page Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"] },
  { key: "page_views_mobile", label: "Mobile Page Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"] },
  { key: "page_views_overview", label: "Overview Page Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "page_views_desktop_overview", label: "Desktop Overview Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "page_views_mobile_overview", label: "Mobile Overview Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "page_views_careers", label: "Careers Page Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "page_views_desktop_careers", label: "Desktop Careers Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "page_views_mobile_careers", label: "Mobile Careers Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "page_views_jobs", label: "Jobs Page Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "page_views_life_at", label: "Life At Page Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "unique_page_views", label: "Unique Page Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  // Share/Post Statistics (8)
  { key: "impressions", label: "Impressions", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "unique_impressions", label: "Unique Impressions", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "clicks", label: "Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "likes", label: "Likes", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "comments", label: "Comments", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "shares", label: "Shares", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "reactions", label: "Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "engagement_rate", label: "Engagement Rate", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "impressions" }, compatibleWidgets: ["kpi", "line-chart", "gauge"] },
  // Reactions breakdown (6)
  { key: "reactions_like", label: "Like Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["bar-chart"] },
  { key: "reactions_celebrate", label: "Celebrate Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["bar-chart"] },
  { key: "reactions_love", label: "Love Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["bar-chart"] },
  { key: "reactions_insightful", label: "Insightful Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["bar-chart"] },
  { key: "reactions_support", label: "Support Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["bar-chart"] },
  { key: "reactions_funny", label: "Funny Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["bar-chart"] },
  // Video Analytics (4)
  { key: "video_views", label: "Video Views (3s+)", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "video_unique_viewers", label: "Unique Video Viewers", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "video_watch_time", label: "Total Watch Time", type: "number", category: "Content", dataType: "Duration", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "video_watch_time_views", label: "Watch Time (3s+ Views)", type: "number", category: "Content", dataType: "Duration", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
];

// ============================================================================
// Dimension Definitions (Subtask 1.3)
// ============================================================================

export const LINKEDIN_PAGES_DIMENSIONS: DimensionDefinition[] = [
  // Time
  { key: "date", label: "Date", type: "Time", exampleValues: ["2026-03-01", "2026-03-02"] },
  { key: "day_of_week", label: "Day of Week", type: "Time", exampleValues: ["Monday", "Tuesday"] },
  { key: "month", label: "Month", type: "Time", exampleValues: ["2026-01", "2026-02"] },
  { key: "week", label: "Week", type: "Time", exampleValues: ["2026-W01", "2026-W02"] },
  // Demographics (lifetime)
  { key: "industry", label: "Follower Industry", type: "Demo", exampleValues: ["Technology", "Finance"] },
  { key: "seniority", label: "Follower Seniority", type: "Demo", exampleValues: ["Senior", "Manager"] },
  { key: "function", label: "Job Function", type: "Demo", exampleValues: ["Engineering", "Sales"] },
  { key: "staff_count_range", label: "Company Size", type: "Demo", exampleValues: ["10001+", "1001-5000"] },
  { key: "location", label: "Follower Location", type: "Geo", exampleValues: ["United States", "UK"] },
  // Note: geo_market_area omitted — LinkedIn followerCountsByGeo returns URNs requiring
  // Bing Geo API resolution. Country-level data available via "location" dimension.
  { key: "association_type", label: "Association Type", type: "Demo", exampleValues: ["EMPLOYEE", "MEMBER"] },
  // Content
  { key: "post_type", label: "Post Type", type: "Content", exampleValues: ["ARTICLE", "IMAGE", "VIDEO"] },
];

// ============================================================================
// LinkedIn Pages Adapter (Subtask 1.1)
// ============================================================================

export const linkedinPagesAdapter: IntegrationAdapter = {
  platform: "linkedin-pages",

  rateLimits: {
    requestsPerMinute: 10,
    requestsPerDay: 100,
  },

  // --------------------------------------------------------------------------
  // fetchMetrics (Subtask 1.4)
  // --------------------------------------------------------------------------
  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const orgId = ctx.accountId;
    if (!orgId) {
      return adapterErr({
        code: "account_not_found",
        message: "No LinkedIn Organization selected. Please select a Company Page first.",
        retryable: false,
      });
    }

    const headers = linkedInHeaders(credentials.accessToken);

    // Clamp date range to 365 days
    const { startMs, endMs, clamped } = clampDateRange(dateRange.start, dateRange.end);

    if (clamped) {
      console.log(JSON.stringify({
        event: "linkedin_pages_date_clamped",
        orgId,
        tenantId: ctx.tenantId,
        originalStart: dateRange.start.toISOString(),
        clampedTo: new Date(startMs).toISOString(),
      }));
    }

    // ---- Fetch org share statistics (daily metrics) with cursor pagination ----
    const allShareStatsElements: OrgShareStatisticsResponse["elements"] = [];
    let shareStatsPageToken: string | undefined;
    const MAX_SHARE_STATS_PAGES = 10; // Safety limit to prevent infinite loops
    let shareStatsPage = 0;

    do {
      const pageTokenParam = shareStatsPageToken ? `&pageToken=${encodeURIComponent(shareStatsPageToken)}` : "";
      const shareStatsUrl = `${LINKEDIN_REST_BASE}/organizationalEntityShareStatistics?q=organizationalEntity&organizationalEntity=urn:li:organization:${orgId}&timeIntervals.timeGranularityType=DAY&timeIntervals.timeRange.start=${startMs}&timeIntervals.timeRange.end=${endMs}${pageTokenParam}`;

      const shareStatsResult = await safeFetch(shareStatsUrl, { headers }, 25_000);

      if (!shareStatsResult.success) {
        return handleLinkedInFetchError(shareStatsResult);
      }

      let shareStatsData: OrgShareStatisticsResponse;
      try {
        shareStatsData = (await shareStatsResult.data.json()) as OrgShareStatisticsResponse;
      } catch (err) {
        return adapterErr({
          code: "invalid_response",
          message: sanitizeErrorMessage(
            `Failed to parse LinkedIn share statistics response: ${err instanceof Error ? err.message : String(err)}`
          ),
          retryable: false,
        });
      }

      allShareStatsElements.push(...(shareStatsData.elements ?? []));

      // Check for next page via paging.links
      const nextLink = shareStatsData.paging?.links?.find((l) => l.rel === "next");
      shareStatsPageToken = nextLink ? new URL(nextLink.href, LINKEDIN_REST_BASE).searchParams.get("pageToken") ?? undefined : undefined;
      shareStatsPage++;
    } while (shareStatsPageToken && shareStatsPage < MAX_SHARE_STATS_PAGES);

    // ---- Fetch follower statistics (lifetime demographics + time-bound gains) ----
    const followerStatsBase = `${LINKEDIN_REST_BASE}/organizationalEntityFollowerStatistics?q=organizationalEntity&organizationalEntity=urn:li:organization:${orgId}`;
    const followerStatsUrl = followerStatsBase; // Lifetime demographics (no time filter)
    const followerGainsUrl = `${followerStatsBase}&timeIntervals.timeGranularityType=DAY&timeIntervals.timeRange.start=${startMs}&timeIntervals.timeRange.end=${endMs}`;

    const followerResult = await safeFetch(followerStatsUrl, { headers }, 15_000);

    let totalFollowers = 0;
    let followersByIndustry: Array<{ industry: string; followerCounts: { organicFollowerCount: number } }> = [];
    let followersBySeniority: Array<{ seniority: string; followerCounts: { organicFollowerCount: number } }> = [];
    let followersByGeo: Array<{ geo: string; followerCounts: { organicFollowerCount: number } }> = [];
    let followersByFunction: Array<{ function: string; followerCounts: { organicFollowerCount: number } }> = [];
    let followersByStaffCountRange: Array<{ staffCountRange: string; followerCounts: { organicFollowerCount: number } }> = [];
    let followersByAssociationType: Array<{ associationType: string; followerCounts: { organicFollowerCount: number } }> = [];

    if (followerResult.success) {
      try {
        const followerData = (await followerResult.data.json()) as OrgFollowerStatisticsResponse;
        if (followerData.elements?.[0]) {
          const el = followerData.elements[0];
          totalFollowers = (el.followerCounts?.organicFollowerCount ?? 0) + (el.followerCounts?.paidFollowerCount ?? 0);
          followersByIndustry = el.followerCountsByIndustry ?? [];
          followersBySeniority = el.followerCountsBySeniority ?? [];
          followersByGeo = el.followerCountsByGeo ?? [];
          followersByFunction = el.followerCountsByFunction ?? [];
          followersByStaffCountRange = el.followerCountsByStaffCountRange ?? [];
          followersByAssociationType = el.followerCountsByAssociationType ?? [];
        }
      } catch {
        console.log(JSON.stringify({
          event: "linkedin_pages_follower_stats_parse_warning",
          orgId,
          tenantId: ctx.tenantId,
        }));
      }
    } else {
      console.log(JSON.stringify({
        event: "linkedin_pages_follower_stats_fetch_warning",
        orgId,
        tenantId: ctx.tenantId,
        errorCode: followerResult.error.code,
      }));
    }

    // Fetch time-bound follower gains (organic vs paid per day)
    const followerGainsResult = await safeFetch(followerGainsUrl, { headers }, 15_000);
    const followerGainsByDate = new Map<string, { organic: number; paid: number }>();

    if (followerGainsResult.success) {
      try {
        const gainsData = (await followerGainsResult.data.json()) as OrgFollowerStatisticsResponse;
        for (const el of gainsData.elements ?? []) {
          if (el.timeRange?.start && el.followerGains) {
            const dateStr = epochMsToDateStr(el.timeRange.start);
            followerGainsByDate.set(dateStr, {
              organic: el.followerGains.organicFollowerGain ?? 0,
              paid: el.followerGains.paidFollowerGain ?? 0,
            });
          }
        }
      } catch {
        console.log(JSON.stringify({
          event: "linkedin_pages_follower_gains_parse_warning",
          orgId,
          tenantId: ctx.tenantId,
        }));
      }
    } else {
      console.log(JSON.stringify({
        event: "linkedin_pages_follower_gains_fetch_warning",
        orgId,
        tenantId: ctx.tenantId,
        errorCode: followerGainsResult.error.code,
      }));
    }

    // ---- Fetch page views (org page statistics) ----
    const pageStatsUrl = `${LINKEDIN_REST_BASE}/organizationPageStatistics?q=organization&organization=urn:li:organization:${orgId}&timeIntervals.timeGranularityType=DAY&timeIntervals.timeRange.start=${startMs}&timeIntervals.timeRange.end=${endMs}`;

    const pageStatsResult = await safeFetch(pageStatsUrl, { headers }, 15_000);

    const pageViewsByDate = new Map<string, PageViewDay>();
    if (pageStatsResult.success) {
      try {
        const pageStatsData = (await pageStatsResult.data.json()) as OrgPageStatisticsResponse;
        for (const el of pageStatsData.elements ?? []) {
          if (el.timeRange?.start) {
            const dateStr = epochMsToDateStr(el.timeRange.start);
            const v = el.pageStatistics?.views;
            pageViewsByDate.set(dateStr, {
              total: v?.allPageViews?.pageViews ?? 0,
              desktop: v?.allDesktopPageViews?.pageViews ?? 0,
              mobile: v?.allMobilePageViews?.pageViews ?? 0,
              overview: v?.overviewPageViews?.pageViews ?? 0,
              desktopOverview: v?.desktopOverviewPageViews?.pageViews ?? 0,
              mobileOverview: v?.mobileOverviewPageViews?.pageViews ?? 0,
              careers: v?.careersPageViews?.pageViews ?? 0,
              desktopCareers: v?.desktopCareersPageViews?.pageViews ?? 0,
              mobileCareers: v?.mobileCareersPageViews?.pageViews ?? 0,
              jobs: v?.jobsPageViews?.pageViews ?? 0,
              lifeAt: v?.lifeAtPageViews?.pageViews ?? 0,
              unique: el.uniquePageViews ?? 0,
            });
          }
        }
      } catch {
        console.log(JSON.stringify({
          event: "linkedin_pages_page_stats_parse_warning",
          orgId,
          tenantId: ctx.tenantId,
        }));
      }
    }

    // ---- Build dailySummary records ----
    const records: Record<string, unknown>[] = [];

    // Sort share stats elements by time
    const sortedElements = [...allShareStatsElements].sort(
      (a, b) => a.timeRange.start - b.timeRange.start
    );

    for (let i = 0; i < sortedElements.length; i++) {
      const el = sortedElements[i]!;
      const dateStr = epochMsToDateStr(el.timeRange.start);
      const stats = el.totalShareStatistics;

      const impressions = stats.impressionCount ?? 0;
      const uniqueImpressions = stats.uniqueImpressionsCount ?? 0;
      const clicks = stats.clickCount ?? 0;
      const likes = stats.likeCount ?? 0;
      const commentCount = stats.commentCount ?? 0;
      const shares = stats.shareCount ?? 0;
      const reactions = likes; // aggregate level — likeCount is proxy
      const engagementRate = impressions > 0
        ? (likes + commentCount + shares) / impressions
        : 0;

      // Follower gains per day (organic vs paid)
      const gains = followerGainsByDate.get(dateStr);
      const organicGain = gains?.organic ?? 0;
      const paidGain = gains?.paid ?? 0;
      const followerChange = organicGain + paidGain;

      // Page view splits
      const pv = pageViewsByDate.get(dateStr) ?? {
        total: 0, desktop: 0, mobile: 0, overview: 0, desktopOverview: 0, mobileOverview: 0,
        careers: 0, desktopCareers: 0, mobileCareers: 0, jobs: 0, lifeAt: 0, unique: 0,
      };

      // Derive month and week dimensions from date
      const dateObj = new Date(dateStr + "T00:00:00Z");
      const month = dateStr.slice(0, 7); // YYYY-MM
      const weekNum = getISOWeek(dateObj);
      const week = `${dateObj.getUTCFullYear()}-W${String(weekNum).padStart(2, "0")}`;

      records.push({
        _reportType: "dailySummary",
        _date: dateStr,
        date: dateStr,
        day_of_week: getDayOfWeek(dateStr),
        month,
        week,
        followers: totalFollowers,
        follower_change: followerChange,
        organic_follower_gain: organicGain,
        paid_follower_gain: paidGain,
        page_views_total: pv.total,
        page_views_desktop: pv.desktop,
        page_views_mobile: pv.mobile,
        page_views_overview: pv.overview,
        page_views_desktop_overview: pv.desktopOverview,
        page_views_mobile_overview: pv.mobileOverview,
        page_views_careers: pv.careers,
        page_views_desktop_careers: pv.desktopCareers,
        page_views_mobile_careers: pv.mobileCareers,
        page_views_jobs: pv.jobs,
        page_views_life_at: pv.lifeAt,
        unique_page_views: pv.unique,
        impressions,
        unique_impressions: uniqueImpressions,
        clicks,
        likes,
        comments: commentCount,
        shares,
        reactions,
        engagement_rate: Math.round(engagementRate * 10000) / 10000,
      });
    }

    // Sort by date
    records.sort((a, b) => String(a.date).localeCompare(String(b.date)));

    // ---- Fetch Top Posts (Subtask 1.8) ----
    const postsUrl = `${LINKEDIN_REST_BASE}/posts?author=urn:li:organization:${orgId}&q=author&count=${POSTS_FETCH_LIMIT}&start=0&sortBy=CREATED`;

    const postsResult = await safeFetch(postsUrl, { headers }, 25_000);

    if (postsResult.success) {
      try {
        const postsData = (await postsResult.data.json()) as LinkedInPostsResponse;
        const posts = postsData.elements ?? [];

        if (posts.length > 0) {
          // Batch fetch share statistics for posts (20 per request)
          const postStatsMap = new Map<string, ShareStatElement["totalShareStatistics"]>();

          for (let batchStart = 0; batchStart < posts.length; batchStart += BATCH_SIZE) {
            const batch = posts.slice(batchStart, batchStart + BATCH_SIZE);
            const sharesParams = batch
              .map((p, idx) => `shares[${idx}]=${encodeURIComponent(p.id)}`)
              .join("&");

            const batchStatsUrl = `${LINKEDIN_REST_BASE}/organizationalEntityShareStatistics?q=organizationalEntity&organizationalEntity=urn:li:organization:${orgId}&${sharesParams}`;

            const batchResult = await safeFetch(batchStatsUrl, { headers }, 15_000);

            if (batchResult.success) {
              try {
                const batchData = (await batchResult.data.json()) as ShareStatisticsResponse;
                for (const el of batchData.elements ?? []) {
                  if (el.share) {
                    postStatsMap.set(el.share, el.totalShareStatistics);
                  }
                }
              } catch {
                // Non-fatal — posts without stats get defaults
              }
            }
          }

          // Build post records with separate engagement scores for sorting
          const postEntries: Array<{ record: Record<string, unknown>; engagement: number }> = [];
          for (const post of posts) {
            const stats = postStatsMap.get(post.id);
            const impressions = stats?.impressionCount ?? 0;
            const clicks = stats?.clickCount ?? 0;
            const likes = stats?.likeCount ?? 0;
            const commentCount = stats?.commentCount ?? 0;
            const shares = stats?.shareCount ?? 0;
            const reactions = likes;
            const totalEngagement = reactions + commentCount + shares + clicks;
            const engagementRate = impressions > 0
              ? (likes + commentCount + shares) / impressions
              : 0;

            const postDate = new Date(post.publishedAt).toISOString();
            const dateStr = postDate.split("T")[0]!;

            // Resolve image URL
            let imageUrl: string | null = null;
            if (post.content?.article?.thumbnail) {
              imageUrl = post.content.article.thumbnail;
            }

            postEntries.push({
              engagement: totalEngagement,
              record: {
                _reportType: "posts",
                _date: dateStr,
                postId: post.id,
                message: truncate(post.commentary ?? "", MESSAGE_TRUNCATE_LENGTH),
                createdTime: postDate,
                postType: getPostType(post),
                imageUrl,
                permalinkUrl: `https://www.linkedin.com/feed/update/${post.id}`,
                impressions,
                clicks,
                likes,
                comments: commentCount,
                shares,
                reactions,
                engagement_rate: Math.round(engagementRate * 10000) / 10000,
              },
            });
          }

          // Sort by total engagement descending, take top 10
          postEntries.sort((a, b) => b.engagement - a.engagement);
          for (const entry of postEntries.slice(0, TOP_POSTS_LIMIT)) {
            records.push(entry.record);
          }
        }
      } catch {
        console.log(JSON.stringify({
          event: "linkedin_pages_posts_parse_warning",
          orgId,
          tenantId: ctx.tenantId,
        }));
      }
    } else {
      console.log(JSON.stringify({
        event: "linkedin_pages_posts_fetch_warning",
        orgId,
        tenantId: ctx.tenantId,
        errorCode: postsResult.error.code,
      }));
    }

    // ---- Fetch video analytics (6-month retention, lifetime aggregates) ----
    // LinkedIn Video Analytics API returns org-level totals — no date range filtering available.
    // VIEWER and TIME_WATCHED metrics don't support daily aggregation per LinkedIn docs.
    // These are lifetime aggregates stored as a single _reportType: "video" record.
    const videoAnalyticsUrl = `${LINKEDIN_REST_BASE}/videoAnalytics?q=entity&entity=urn:li:organization:${orgId}`;
    const videoResult = await safeFetch(videoAnalyticsUrl, { headers }, 15_000);

    if (videoResult.success) {
      try {
        const videoData = (await videoResult.data.json()) as VideoAnalyticsResponse;
        const videoMetrics: Record<string, number> = {};
        for (const el of videoData.elements ?? []) {
          videoMetrics[el.type] = el.value ?? 0;
        }
        records.push({
          _reportType: "video",
          video_views: videoMetrics["VIDEO_VIEW"] ?? 0,
          video_unique_viewers: videoMetrics["VIEWER"] ?? 0,
          video_watch_time: videoMetrics["TIME_WATCHED"] ?? 0,
          video_watch_time_views: videoMetrics["TIME_WATCHED_FOR_VIDEO_VIEWS"] ?? 0,
        });
      } catch {
        console.log(JSON.stringify({
          event: "linkedin_pages_video_analytics_parse_warning",
          orgId,
          tenantId: ctx.tenantId,
        }));
      }
    } else {
      console.log(JSON.stringify({
        event: "linkedin_pages_video_analytics_fetch_warning",
        orgId,
        tenantId: ctx.tenantId,
        errorCode: videoResult.error.code,
      }));
    }

    // ---- Fetch per-post reaction type breakdown ----
    // Reactions API is per-post with pagination; aggregate counts across top posts
    let reactionsCallCount = 0;

    const reactionCounts: Record<string, number> = {
      LIKE: 0, PRAISE: 0, EMPATHY: 0, INTEREST: 0, APPRECIATION: 0, ENTERTAINMENT: 0,
    };
    const postRecords = records.filter((r) => r._reportType === "posts");
    for (const postRecord of postRecords) {
      const postUrn = postRecord.postId as string;
      if (!postUrn) continue;
      if (reactionsCallCount >= MAX_REACTIONS_CALLS) break;

      let reactionsStart = 0;

      do {
        const reactionsUrl = `${LINKEDIN_REST_BASE}/reactions/(entity:${encodeURIComponent(postUrn)})?q=entity&count=${REACTIONS_PAGE_SIZE}&start=${reactionsStart}`;
        const reactionsResult = await safeFetch(reactionsUrl, { headers }, 10_000);
        reactionsCallCount++;

        if (!reactionsResult.success) break;

        try {
          const reactionsData = (await reactionsResult.data.json()) as ReactionsResponse;
          const elements = reactionsData.elements ?? [];
          for (const el of elements) {
            if (el.reactionType && el.reactionType in reactionCounts) {
              reactionCounts[el.reactionType]!++;
            }
          }

          // Stop if fewer elements than page size (last page)
          if (elements.length < REACTIONS_PAGE_SIZE) break;
          reactionsStart += REACTIONS_PAGE_SIZE;
        } catch {
          // Non-fatal — skip reaction parsing for this post
          break;
        }
      } while (reactionsCallCount < MAX_REACTIONS_CALLS);
    }

    records.push({
      _reportType: "reactions",
      reactions_like: reactionCounts["LIKE"],
      reactions_celebrate: reactionCounts["PRAISE"],
      reactions_love: reactionCounts["EMPATHY"],
      reactions_insightful: reactionCounts["INTEREST"],
      reactions_support: reactionCounts["APPRECIATION"],
      reactions_funny: reactionCounts["ENTERTAINMENT"],
    });

    // ---- Add demographics records (6 facets) ----
    if (totalFollowers > 0) {
      const addDemographic = (dimension: string, value: string, count: number) => {
        records.push({
          _reportType: "demographics",
          dimension,
          value,
          follower_count: count,
          percentage: totalFollowers > 0 ? Math.round((count / totalFollowers) * 1000) / 10 : 0,
        });
      };

      for (const item of followersByIndustry) {
        addDemographic("industry", item.industry, item.followerCounts?.organicFollowerCount ?? 0);
      }
      for (const item of followersBySeniority) {
        addDemographic("seniority", item.seniority, item.followerCounts?.organicFollowerCount ?? 0);
      }
      for (const item of followersByGeo) {
        addDemographic("location", item.geo, item.followerCounts?.organicFollowerCount ?? 0);
      }
      for (const item of followersByFunction) {
        addDemographic("function", item.function, item.followerCounts?.organicFollowerCount ?? 0);
      }
      for (const item of followersByStaffCountRange) {
        addDemographic("staff_count_range", item.staffCountRange, item.followerCounts?.organicFollowerCount ?? 0);
      }
      for (const item of followersByAssociationType) {
        addDemographic("association_type", item.associationType, item.followerCounts?.organicFollowerCount ?? 0);
      }
    }

    return adapterOk({
      platform: "linkedin-pages",
      accountId: orgId,
      dateRange: { start: dateRange.start, end: dateRange.end },
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  // --------------------------------------------------------------------------
  // validateConnection (Subtask 1.5)
  // --------------------------------------------------------------------------
  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const orgId = ctx.accountId;
    if (!orgId) {
      return adapterErr({
        code: "account_not_found",
        message: "No LinkedIn Organization ID provided for connection validation.",
        retryable: false,
      });
    }

    const headers = linkedInHeaders(credentials.accessToken);
    const url = `${LINKEDIN_REST_BASE}/organizations/${orgId}`;
    const result = await safeFetch(url, { headers }, 5_000);

    if (!result.success) {
      return handleLinkedInFetchError(result);
    }

    return adapterOk(true);
  },

  // --------------------------------------------------------------------------
  // discoverAccounts (Subtask 1.6 — catalog contract per Story 31.21)
  // --------------------------------------------------------------------------
  async discoverAccounts(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Scope pre-flight (Story 31.21 AC #6 — parity with the ads adapter):
    // reads credentials.scopes (token truth), never AccountInfo.scopes
    const requiredScopes = LINKEDIN_INTEGRATION_SCOPES["linkedin-pages"].scopes;
    const grantedScopes = new Set(credentials.scopes);
    const missingScopes = requiredScopes.filter((s) => !grantedScopes.has(s));
    if (missingScopes.length > 0) {
      return adapterErr({
        code: "permission_denied",
        message: `LinkedIn Pages requires the following missing scope(s): ${missingScopes.join(", ")}. Please reconnect LinkedIn with Pages permissions.`,
        retryable: false,
      });
    }

    // Pages family ONLY — zero ad-account endpoint requests (Story 31.21 AC #2)
    const discoveryResult = await discoverLinkedInOrganizations({
      accessToken: credentials.accessToken,
      tenantId: ctx.tenantId,
      integrationId: ctx.integrationId,
    });

    if (!discoveryResult.ok) {
      // Strict semantics (Story 31.21 AC #4): discovery failure is adapterErr,
      // never a silent empty catalog. Route through parseLinkedInError for
      // per-cause codes (65604 → token_invalid, etc.) with safeFetch fallback.
      return handleLinkedInFetchError({ success: false, error: discoveryResult.error });
    }

    // Defensive dedupe by id — first occurrence wins (ADR-0004 §Anti-Patterns 4)
    const dedupedOrgs = new Map<string, DiscoveredOrganization>();
    for (const org of discoveryResult.organizations) {
      if (!dedupedOrgs.has(org.id)) dedupedOrgs.set(org.id, org);
    }

    const accounts: AccountInfo[] = [...dedupedOrgs.values()].map((org) => ({
      id: org.id,
      name: org.name,
      platform: "linkedin-pages" as const,
      metadata: {},
      // Display/consent metadata ONLY (31-4 contract) — fresh array per entry,
      // sourced from the single source of truth (no hardcoded drift)
      scopes: [...LINKEDIN_INTEGRATION_SCOPES["linkedin-pages"].scopes],
    }));

    return adapterOk(accounts);
  },

  // --------------------------------------------------------------------------
  // refreshToken (Subtask 1.9)
  // --------------------------------------------------------------------------
  async refreshToken(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<RefreshedTokens>> {
    if (!credentials.refreshToken) {
      return adapterErr({
        code: "token_expired",
        message: "No refresh token available. User must re-authorize LinkedIn.",
        retryable: false,
      });
    }

    const env = ctx.env as {
      LINKEDIN_CLIENT_ID?: string;
      LINKEDIN_CLIENT_SECRET?: string;
    };

    if (!env.LINKEDIN_CLIENT_ID || !env.LINKEDIN_CLIENT_SECRET) {
      return adapterErr({
        code: "invalid_response",
        message: "LinkedIn client credentials not configured",
        retryable: false,
      });
    }

    const result = await refreshLinkedInAccessToken(
      credentials.refreshToken,
      env.LINKEDIN_CLIENT_ID,
      env.LINKEDIN_CLIENT_SECRET
    );

    if (result.status === "error") {
      return adapterErr({
        code: "token_expired",
        message: result.error ?? "Failed to refresh LinkedIn token",
        retryable: false,
      });
    }

    if (!result.accessToken || result.expiresIn === undefined) {
      return adapterErr({
        code: "invalid_response",
        message: "LinkedIn token refresh returned incomplete data",
        retryable: false,
      });
    }

    const expiresAt = new Date(Date.now() + result.expiresIn * 1000);

    return adapterOk({
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expiresAt,
    });
  },

  // --------------------------------------------------------------------------
  // getAvailableMetrics (Subtask 1.7)
  // --------------------------------------------------------------------------
  getAvailableMetrics(): MetricDefinition[] {
    return LINKEDIN_PAGES_METRICS;
  },

  // --------------------------------------------------------------------------
  // getAvailableDimensions (Subtask 1.7)
  // --------------------------------------------------------------------------
  getAvailableDimensions(): DimensionDefinition[] {
    return LINKEDIN_PAGES_DIMENSIONS;
  },
};
